import { Component } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Week, WeeksService } from 'src/app/services/weeks.service';

@Component({
  selector: 'app-weeks',
  templateUrl: './weeks.component.html',
  styleUrls: ['./weeks.component.css']
})
export class WeeksComponent {
  items: Week[] = [];
  loading = false;
  open = false;
  mode: 'add' | 'edit' | 'view' = 'add';
  selectedWeek: Week | null = null;
  isError = false;
  errorMessage = '';
  form = new FormGroup({
    name: new FormControl(''),
    ordinal: new FormControl<number | null>(null),
    startDate: new FormControl(''),
    endDate: new FormControl(''),
    active: new FormControl(false),
  });

  constructor(private weeksService: WeeksService) { }

  async ngOnInit() {
    await this.fetchItems();
  }

  async fetchItems() {
    this.loading = true;
    try {
      this.items = await this.weeksService.list();
    } catch (error: any) {
      this.showError(error?.error?.errorMessages?.[0]?.message || 'WEEKS.LOAD_FAILED');
      this.items = [];
    } finally {
      this.loading = false;
    }
  }

  formatDate(value: string): string {
    return value || '—';
  }

  onAdd() {
    this.form.enable();
    this.mode = 'add';
    this.selectedWeek = null;
    this.form.reset({
      name: '',
      ordinal: this.nextOrdinal,
      startDate: '',
      endDate: '',
      active: false,
    });
    this.isError = false;
    this.open = true;
  }

  onEdit(week: Week) {
    this.form.enable();
    this.mode = 'edit';
    this.selectedWeek = week;
    this.form.reset({
      name: week.name || '',
      ordinal: week.ordinal ?? null,
      startDate: week.startDate || '',
      endDate: week.endDate || '',
      active: week.active === true,
    });
    this.isError = false;
    this.open = true;
  }

  openRow(week: Week) {
    this.form.enable();
    this.mode = 'view';
    this.selectedWeek = week;
    this.form.reset({
      name: week.name || '',
      ordinal: week.ordinal ?? null,
      startDate: week.startDate || '',
      endDate: week.endDate || '',
      active: week.active === true,
    });
    this.isError = false;
    this.open = true;
    this.form.disable();
  }

  async setActive(week: Week, active: boolean) {
    if (week.active === active) {
      return;
    }
    try {
      await this.weeksService.update(week.id, { active });
      await this.fetchItems();
    } catch (error: any) {
      this.showError(error?.error?.errorMessages?.[0]?.message || 'WEEKS.SAVE_FAILED');
    }
  }

  async submit() {
    if (this.mode === 'view') {
      return;
    }
    const ordinal = this.form.value.ordinal;
    const body = {
      name: this.form.value.name || '',
      ordinal: ordinal === null || ordinal === undefined ? undefined : Number(ordinal),
      startDate: this.form.value.startDate || '',
      endDate: this.form.value.endDate || '',
      active: this.form.value.active === true,
    };

    try {
      if (this.mode === 'edit' && this.selectedWeek) {
        await this.weeksService.update(this.selectedWeek.id, body);
      } else {
        await this.weeksService.create(body);
      }
      this.open = false;
      this.selectedWeek = null;
      await this.fetchItems();
    } catch (error: any) {
      this.showError(error?.error?.errorMessages?.[0]?.message || 'WEEKS.SAVE_FAILED');
    }
  }

  closeModal() {
    this.open = false;
    this.selectedWeek = null;
    this.isError = false;
    this.form.enable();
  }

  showError(message: string) {
    this.isError = true;
    this.errorMessage = message;
  }

  private get nextOrdinal(): number {
    return this.items.reduce((max, item) => Math.max(max, item.ordinal || 0), 0) + 1;
  }
}
